/**
 * Performance Monitoring Utility
 * 
 * Tracks and reports key performance metrics for the TENSION app
 */

// ============================================================================
// PERFORMANCE METRICS TRACKING
// ============================================================================

class PerformanceMonitor {
  private marks: Map<string, number> = new Map();
  private metrics: Map<string, number> = new Map();

  /**
   * Mark a performance timing point
   */
  mark(name: string): void {
    this.marks.set(name, performance.now());
    
    // Use native Performance API if available
    if ('mark' in performance) {
      performance.mark(name);
    }
  }

  /**
   * Measure time between two marks
   */
  measure(name: string, startMark: string, endMark?: string): number | null {
    const start = this.marks.get(startMark);
    if (!start) {
      console.warn(`Performance mark "${startMark}" not found`);
      return null;
    }

    const end = endMark ? this.marks.get(endMark) : performance.now();
    if (endMark && !end) {
      console.warn(`Performance mark "${endMark}" not found`);
      return null;
    }

    const duration = (end || performance.now()) - start;
    this.metrics.set(name, duration);

    // Use native Performance API if available
    if ('measure' in performance && endMark) {
      try {
        performance.measure(name, startMark, endMark);
      } catch (e) {
        // Silently fail if marks don't exist
      }
    }

    return duration;
  }

  /**
   * Get a specific metric
   */
  getMetric(name: string): number | undefined {
    return this.metrics.get(name);
  }

  /**
   * Get all metrics
   */
  getAllMetrics(): Record<string, number> {
    return Object.fromEntries(this.metrics);
  }

  /**
   * Log performance summary to console
   */
  logSummary(): void {
    console.group('⚡ Performance Metrics');
    
    const metrics = this.getAllMetrics();
    Object.entries(metrics).forEach(([name, duration]) => {
      const color = duration < 100 ? '🟢' : duration < 500 ? '🟡' : '🔴';
      console.log(`${color} ${name}: ${duration.toFixed(2)}ms`);
    });

    // Log Web Vitals if available
    this.logWebVitals();

    console.groupEnd();
  }

  /**
   * Log Web Vitals (LCP, FID, CLS)
   */
  private logWebVitals(): void {
    if ('PerformanceObserver' in window) {
      try {
        // Largest Contentful Paint (LCP)
        new PerformanceObserver((list) => {
          const entries = list.getEntries();
          const lastEntry = entries[entries.length - 1] as any;
          console.log(`📊 LCP: ${lastEntry.renderTime || lastEntry.loadTime}ms`);
        }).observe({ entryTypes: ['largest-contentful-paint'] });

        // First Input Delay (FID)
        new PerformanceObserver((list) => {
          const entries = list.getEntries();
          entries.forEach((entry: any) => {
            console.log(`📊 FID: ${entry.processingStart - entry.startTime}ms`);
          });
        }).observe({ entryTypes: ['first-input'] });

        // Cumulative Layout Shift (CLS)
        new PerformanceObserver((list) => {
          let clsScore = 0;
          list.getEntries().forEach((entry: any) => {
            if (!entry.hadRecentInput) {
              clsScore += entry.value;
            }
          });
          console.log(`📊 CLS: ${clsScore.toFixed(4)}`);
        }).observe({ entryTypes: ['layout-shift'] });
      } catch (e) {
        // Browser doesn't support these APIs
      }
    }
  }

  /**
   * Get bundle size estimates from navigation timing
   */
  getBundleMetrics(): {
    transferSize: number;
    encodedBodySize: number;
    decodedBodySize: number;
  } | null {
    if ('getEntriesByType' in performance) {
      const navigationEntries = performance.getEntriesByType('navigation') as any[];
      if (navigationEntries.length > 0) {
        const entry = navigationEntries[0];
        return {
          transferSize: entry.transferSize || 0,
          encodedBodySize: entry.encodedBodySize || 0,
          decodedBodySize: entry.decodedBodySize || 0,
        };
      }
    }
    return null;
  }

  /**
   * Log bundle size information
   */
  logBundleSize(): void {
    const metrics = this.getBundleMetrics();
    if (metrics) {
      console.group('📦 Bundle Size');
      console.log(`Transfer Size: ${(metrics.transferSize / 1024).toFixed(2)} KB`);
      console.log(`Encoded Size: ${(metrics.encodedBodySize / 1024).toFixed(2)} KB`);
      console.log(`Decoded Size: ${(metrics.decodedBodySize / 1024).toFixed(2)} KB`);
      console.groupEnd();
    }
  }

  /**
   * Check if the app is running slow
   */
  isRunningslow(): boolean {
    // Check if any critical metric is above threshold
    const criticalMetrics = ['route-load', 'component-render', 'data-fetch'];
    
    for (const metricName of criticalMetrics) {
      const duration = this.metrics.get(metricName);
      if (duration && duration > 1000) {
        return true;
      }
    }

    return false;
  }

  /**
   * Clear all metrics
   */
  clear(): void {
    this.marks.clear();
    this.metrics.clear();
  }
}

// ============================================================================
// SINGLETON INSTANCE
// ============================================================================

export const performanceMonitor = new PerformanceMonitor();

// ============================================================================
// CONVENIENCE FUNCTIONS
// ============================================================================

/**
 * Track component mount time
 */
export function trackComponentMount(componentName: string): () => void {
  const startMark = `${componentName}-mount-start`;
  performanceMonitor.mark(startMark);

  return () => {
    const endMark = `${componentName}-mount-end`;
    performanceMonitor.mark(endMark);
    performanceMonitor.measure(`${componentName} Mount`, startMark, endMark);
  };
}

/**
 * Track async operation time
 */
export async function trackAsync<T>(
  name: string,
  operation: () => Promise<T>
): Promise<T> {
  const startMark = `${name}-start`;
  const endMark = `${name}-end`;

  performanceMonitor.mark(startMark);
  
  try {
    const result = await operation();
    performanceMonitor.mark(endMark);
    performanceMonitor.measure(name, startMark, endMark);
    return result;
  } catch (error) {
    performanceMonitor.mark(endMark);
    performanceMonitor.measure(`${name} (failed)`, startMark, endMark);
    throw error;
  }
}

/**
 * Log performance report (call after app has loaded)
 */
export function logPerformanceReport(): void {
  // Wait a bit for metrics to settle
  setTimeout(() => {
    performanceMonitor.logSummary();
    performanceMonitor.logBundleSize();
    
    if (performanceMonitor.isRunningslow()) {
      console.warn('⚠️ App performance is degraded. Check metrics above.');
    }
  }, 2000);
}

// ============================================================================
// AUTO-TRACK PAGE LOAD
// ============================================================================

if (typeof window !== 'undefined') {
  // Track when DOM content is loaded
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      performanceMonitor.mark('dom-content-loaded');
    });
  } else {
    performanceMonitor.mark('dom-content-loaded');
  }

  // Track when page is fully loaded
  window.addEventListener('load', () => {
    performanceMonitor.mark('page-load-complete');
    
    // Auto-log performance after load (only in development)
    if (import.meta.env.DEV) {
      logPerformanceReport();
    }
  });
}